import { CascadeMenu } from '@aibees/elephant/components/cascade-menu';
import { ExportSearchGrouping } from '@aibees/elephant/hooks';
import { ROUTES as R } from '@aibees/elephant/router';
import { IconButton } from '@aibees/shared/components';
import { ExportStatus } from '@aibees/shared/components/status-tag';
import { classnames } from '@aibees/shared/utils';
import { memo, useCallback, useMemo } from 'react';
import type { ExportTableViewRecordsActionProps as Props } from './types';

const CASCADE_ITEMS = [
  {
    key: 'include',
    label: 'Include',
    icon: 'add-circle-outlined' as const,
    iconColor: 'primary.500',
  },
  {
    key: 'exclude',
    label: 'Exclude',
    icon: 'stop-sign' as const,
    iconColor: 'error.500',
  },
  {
    key: 'include_and',
    label: 'Include (AND)',
    icon: 'add-task' as const,
    iconColor: 'warning.500',
  },
];

const URL_KEY: Record<string, { company: string; people: string }> = {
  include: {
    company: 'companies_exports_request_include',
    people: 'people_exports_request_include',
  },
  exclude: {
    company: 'companies_exports_request_exclude',
    people: 'people_exports_request_exclude',
  },
  include_and: {
    company: 'companies_exports_request_all_of',
    people: 'people_exports_request_all_of',
  },
};

export const ExportTableViewRecordsAction = memo(
  ({ className, item }: Props) => {
    const disabled =
      item.status === ExportStatus.FAILED ||
      item.status === ExportStatus.PROCESSING;

    const handleSelect = useCallback(
      (parentKey: string) => {
        const subKey =
          item.grouping === ExportSearchGrouping.COMPANY ? 'company' : 'people';
        const urlKey = URL_KEY[parentKey]?.[subKey];
        if (!urlKey) return;
        const route = subKey === 'company' ? R.searchCompany : R.searchPeople;
        const value = encodeURIComponent(
          `${item.name}::${encodeURIComponent(item.id)}`
        );
        window.open(`${route}?${urlKey}=${value}`, '_blank');
      },
      [item]
    );

    const trigger = useMemo(
      () => (
        <IconButton
          icon="eye-outlined"
          variant="outlined"
          size="xsmall"
          disabled={disabled}
        />
      ),
      [disabled]
    );

    if (disabled) {
      return (
        <span className={classnames('ExportTableViewRecordsAction', className)}>
          {trigger}
        </span>
      );
    }

    return (
      <CascadeMenu
        className={classnames('ExportTableViewRecordsAction', className)}
        items={CASCADE_ITEMS}
        onSelect={handleSelect}
        buttonElement={trigger}
      />
    );
  }
);

ExportTableViewRecordsAction.displayName = 'ExportTableViewRecordsAction';
